/**
 * 🛡️ Error Boundary
 *
 * يلتقط أخطاء JavaScript في React components ويعرض fallback UI
 * بدل ما تنهار كل الصفحة (white screen).
 *
 * Use case:
 *  - chunk loading fails (network issue)
 *  - runtime error في component (undefined.method())
 *  - errors في useEffect أو render
 *
 * Usage:
 *   <ErrorBoundary>
 *     <YourPage />
 *   </ErrorBoundary>
 */

import { Component, type ErrorInfo, type ReactNode } from 'react';
import { motion } from 'framer-motion';
import { AlertTriangle, Home, RefreshCw } from 'lucide-react';

interface Props {
  children: ReactNode;
  /** custom fallback (optional) */
  fallback?: (error: Error, reset: () => void) => ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  override state: State = {
    hasError: false,
    error: null,
  };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  override componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // eslint-disable-next-line no-console
    console.error('🛡️ [ErrorBoundary] Caught error:', error, errorInfo);
  }

  reset = (): void => {
    this.setState({ hasError: false, error: null });
  };

  override render(): ReactNode {
    if (this.state.hasError && this.state.error) {
      // Custom fallback
      if (this.props.fallback) {
        return this.props.fallback(this.state.error, this.reset);
      }

      // Default fallback
      return <DefaultErrorFallback error={this.state.error} onReset={this.reset} />;
    }

    return this.props.children;
  }
}

// ═══════════════════════════════════════════════════
// Default Fallback UI
// ═══════════════════════════════════════════════════

function DefaultErrorFallback({
  error,
  onReset,
}: {
  error: Error;
  onReset: () => void;
}) {
  // كشف نوع الخطأ
  const isChunkLoadError =
    error.message.includes('chunk') ||
    error.message.includes('dynamically imported module') ||
    error.message.includes('Failed to fetch');

  const goHome = () => {
    onReset();
    window.location.href = '/';
  };

  const reload = () => {
    window.location.reload();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-white to-amber-50 flex items-center justify-center p-6">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="max-w-md w-full bg-white rounded-2xl shadow-2xl p-8 border-2 border-rose-200"
      >
        <div className="flex justify-center mb-4">
          <div className="p-4 bg-rose-100 rounded-full">
            <AlertTriangle className="h-10 w-10 text-rose-600" />
          </div>
        </div>

        <h2 className="text-2xl font-bold text-center text-rose-800 mb-2">
          عذراً، حصل خطأ غير متوقع
        </h2>

        <p className="text-center text-sm text-zinc-600 mb-4">
          {isChunkLoadError
            ? '⚡ يبدو أن هناك تحديثاً جديداً للموقع. حدّث الصفحة لتحميل النسخة الأحدث.'
            : '🐛 حصل خطأ في الصفحة. جرّب العودة للرئيسية أو تحديث الصفحة.'}
        </p>

        {/* Error details (collapsible) */}
        <details className="mb-5 bg-zinc-50 rounded-lg p-3 text-xs">
          <summary className="cursor-pointer text-zinc-600 font-medium">
            📋 التفاصيل التقنية
          </summary>
          <div className="mt-2 space-y-2">
            <div>
              <strong className="text-zinc-700">الرسالة:</strong>
              <p className="font-mono text-rose-700 mt-1 break-all">
                {error.message}
              </p>
            </div>
            {error.stack && (
              <div>
                <strong className="text-zinc-700">Stack:</strong>
                <pre className="font-mono text-[10px] text-zinc-500 mt-1 overflow-x-auto whitespace-pre-wrap break-all max-h-32 overflow-y-auto">
                  {error.stack.split('\n').slice(0, 5).join('\n')}
                </pre>
              </div>
            )}
          </div>
        </details>

        {/* Actions */}
        <div className="flex flex-col gap-2">
          <button
            onClick={reload}
            className="w-full px-4 py-2.5 bg-gradient-to-r from-brand-purple-600 to-pink-600 text-white rounded-lg hover:opacity-90 flex items-center justify-center gap-2 font-medium"
          >
            <RefreshCw className="h-4 w-4" />
            {isChunkLoadError ? 'تحديث الموقع' : 'إعادة تحميل الصفحة'}
          </button>
          <button
            onClick={goHome}
            className="w-full px-4 py-2.5 bg-zinc-100 text-zinc-700 hover:bg-zinc-200 rounded-lg flex items-center justify-center gap-2 font-medium"
          >
            <Home className="h-4 w-4" />
            العودة للرئيسية
          </button>
        </div>

        <p className="text-center text-[10px] text-zinc-400 mt-4">
          لو استمرت المشكلة، تواصل مع الدعم
        </p>
      </motion.div>
    </div>
  );
}
